import { intersectionBy, orderBy } from 'lodash';
import { SvgIcon } from '@common-components';
import { WarningAmber as WarningAmberIcon } from '@icons';
import { ActivityType, SubmissionMarketRequestStatus } from 'enums';
import { messages } from 'i18n';
import { ActivityLog, ExtendedMarketRequest } from 'types';
import { calculateDaysDiff } from 'utils';

export const mapMarketRequestToEmailActivity = (
  marketRequest: ExtendedMarketRequest,
  activities: ActivityLog[],
): ActivityLog | undefined => {
  const logEntries = orderBy(activities, 'date', 'desc');
  switch (marketRequest.status) {
    case SubmissionMarketRequestStatus.AwaitingQuote:
      return logEntries.find((log) => ActivityType.RequestForQuote === log.activityType);
    case SubmissionMarketRequestStatus.QuoteReady:
      return logEntries.find((log) => ActivityType.BindRequest === log.activityType);
    case SubmissionMarketRequestStatus.Bound:
      return logEntries.find((log) => ActivityType.PolicyRequested === log.activityType);
    default:
      return undefined;
  }
};

export const getQuoteReadyTitleStartIcon = (marketRequest: ExtendedMarketRequest): typeof SvgIcon | undefined => {
  if (!marketRequest.quote?.expiresAt) {
    return undefined;
  }
  return calculateDaysDiff(marketRequest.quote?.expiresAt) <= 0 ? WarningAmberIcon : undefined;
};

export const getQuoteExpirationSubtitle = (marketRequest: ExtendedMarketRequest) => {
  if (!marketRequest.quote?.expiresAt) {
    return undefined;
  }
  const daysDiff = calculateDaysDiff(marketRequest.quote?.expiresAt);
  if (daysDiff < 0) {
    return `* ${messages.market.expiredOn(marketRequest.quote?.expiresAt)}`;
  }
  if (daysDiff > 0) {
    return `* ${messages.market.expiresIn(daysDiff)}`;
  }
  return `* ${messages.market.expiresToday}`;
};

export const getQuoteCoverageLines = (marketRequest: ExtendedMarketRequest) => {
  const quoteCoverageLines = marketRequest.quote?.coverageLines;

  if (quoteCoverageLines && quoteCoverageLines.length > 0) {
    return quoteCoverageLines;
  }

  const productCoverageLines = marketRequest.insuranceProduct?.coverageLines.map((item) => item.coverageLine);

  if (!productCoverageLines) {
    return [];
  }

  if (marketRequest.submissionCoverageLines) {
    return intersectionBy(marketRequest.submissionCoverageLines, productCoverageLines);
  }

  return [];
};
